/**
 * Modern Theme Definition
 * Contemporary design with navy/purple color palette and sleek aesthetics
 * Provides a polished, professional alternative to the retro theme
 */

import type { Theme } from './types';

/**
 * Modern theme object
 * Features a sophisticated navy and purple color scheme with Space Grotesk font,
 * smooth gradients, refined shadows, and elegant spacing inspired by professional DAWs
 */
export const modernTheme: Theme = {
  name: 'modern',

  /**
   * Color palette
   * Deep navy background with vibrant purple accents for a contemporary feel
   */
  colors: {
    // Background colors - deep navy for sophistication
    bg: '#0a0e27',
    bgSecondary: '#0f1729',
    surface: '#1a1f3a',

    // Foreground colors - soft white for readability
    fg: '#e8eaf6',
    fgSecondary: '#b0b3c1',

    // Accent colors - vibrant purple gradient
    accent: '#667eea',
    accentHover: '#7c8ff0',
    accentActive: '#5568d3',

    // UI element colors
    border: '#2d3748',
    borderHover: '#4a5568',
    cellBg: '#1a1f3a',
    cellBorder: '#2d3748',
    cellBorderActive: '#667eea',

    // Semantic colors - modern status indicators
    success: '#48bb78',
    warning: '#ed8936',
    error: '#f56565',
    info: '#4299e1',
  },

  /**
   * Typography
   * Rajdhani font family for bold, condensed DAW-style text rendering
   */
  typography: {
    fontFamily: "'Rajdhani', 'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  /**
   * Spacing
   * Modern spacing scale for balanced layouts
   */
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },

  /**
   * Border radius
   * Smooth, rounded corners for contemporary design
   */
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  /**
   * Shadows
   * Subtle elevation with accent glow effects
   */
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    glow: '0 0 20px rgba(102, 126, 234, 0.4)',
  },

  /**
   * Transitions
   * Smooth, polished animations with cubic-bezier easing
   */
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
};
